import styled, { css } from 'styled-components';
import rem from 'polished/lib/helpers/rem';
import Modal from 'react-modal';
// components
import Button from '../Button';
import { Col, Container, Row } from '../../foundations/Grid';
import Dismiss from '../Dismiss';
import { getSectionBackgroundColor } from '../SectionContainer/styles';

// define (non-ImmersiveFlow) modal dimensions
// next rendition of the modal will be to use dynamic/provided dimensions (or at least maxHeight, but maybe retain defined small/large defined width) using these values as fallback defaults?
export const MODAL_WIDTH = {
  small: 560,
  large: 800,
};

export const MODAL_HEIGHT = {
  small: 300,
  large: 600,
};

export const getModalHeightThreshold = (variant) => {
  // default to large modal height
  return variant !== 'large' ? MODAL_HEIGHT.small : MODAL_HEIGHT.large;
};

export const ModalStyled = styled(Modal)`
  ${({
    backgroundVariant,
    branding,
    theme,
    themeType,
    variant,
    windowInnerHeight,
  }) => {
    let thisWidth = 'auto';
    let thisMaxWidth = variant === 'large' ? rem(MODAL_WIDTH.large) : rem(MODAL_WIDTH.small);

    const modalHeightThreshold = getModalHeightThreshold(variant);
    let thisHeight;
    if (windowInnerHeight < modalHeightThreshold) {
      thisHeight = 'auto';
    } else {
      thisHeight = 'fit-content';
    }
    let thisMaxHeight = rem(modalHeightThreshold);
    let thisInset = rem(24);
    let thisBoxShadow = theme.effects.shadows.dp8;
    let thisBorderRadius = rem(4);

    // ImmersiveFlow overrides
    if (variant === 'immersiveFlow') {
      thisMaxWidth = 'none';
      thisWidth = '100%';
      thisHeight = '100%';
      thisMaxHeight = '100%';
      thisInset = 'unset';
      thisBoxShadow = 'none';
      thisBorderRadius = 'unset';
    }

    return css`
      width: ${thisWidth};
      max-width: ${thisMaxWidth};
      height: ${thisHeight};
      max-height: ${thisMaxHeight};
      inset: ${thisInset};
      overflow: auto;
      background-color: ${getSectionBackgroundColor(backgroundVariant, branding, theme, themeType)};
      box-shadow: ${thisBoxShadow};
      border-radius: ${thisBorderRadius};
    `;
  }}
`;

export const ContainerStyledModal = styled(Container)`
  ${({ theme, variant }) => {
    const mediumUp = theme.mediumUp();
    const xlargeUp = theme.xlargeUp();
    let thisMaxWidth = xlargeUp ? rem(MODAL_WIDTH.large + 100) : rem(MODAL_WIDTH.large);
    const thisMinHeight = variant === 'large' ? 'auto' : rem(MODAL_HEIGHT.small);

    // ImmersiveFlow overRides
    if (variant === 'immersiveFlow') {
      thisMaxWidth = 'unset';
    }

    return css`
      padding: 0;
      position: relative;
      max-width: ${thisMaxWidth};
      height: auto; /* WARNING: Setting this height value to 100% will break the modal in Safari and Mac OS devices and it will not display */
      min-height: ${mediumUp ? thisMinHeight : 0};
      margin: 0 auto;
      border: 0;
      border-radius: ${mediumUp ? rem(4) : 0};
      backface-visibility: hidden;
      box-shadow: none;
      overflow-y: ${variant === 'immersiveFlow' ? 'clip' : 'auto'};
      z-index: ${theme.zIndex.modalMain};
    `;
  }}
`;

export const RowStyledContent = styled(Row)`
  ${({ modalFooter, windowInnerHeight, variant }) => {
    let thisDisplay = 'block';
    let thisHeight = getModalHeightThreshold(variant);
    if (modalFooter) {
      thisHeight = thisHeight - 80;
    }
    thisHeight = rem(thisHeight);
    // thisHeight = 'auto'; // revisit if contentHeight is less than getModalHeightThreshold, set to auto
    // ImmersiveFlow overrides
    if (variant === 'immersiveFlow') {
      thisDisplay = 'flex';
      thisHeight = '100%';
    }
    return css`
      position: relative;
      display: ${thisDisplay};
      width: 100%;
      height: ${thisHeight};
      overflow-y: auto;
      ${variant === 'immersiveFlow' && { alignItems: 'center' }}
    `;
  }}
`;

export const ColStyledContent = styled(Col)`
  ${({ modalFooter, modalHeader, prevCtaControl, theme, variant }) => {
    const mediumUp = theme.mediumUp();

    const thisDefaultPadding = mediumUp ? 48 : 16;
    let thisPaddingBottomPx = thisDefaultPadding;

    if (modalFooter) {
      thisPaddingBottomPx += 80;
    }

    return css`
      height: 100%;
      padding: ${rem(thisDefaultPadding)} ${rem(thisDefaultPadding)} ${rem(thisPaddingBottomPx)};

      ${variant === 'immersiveFlow' && (() => {
    // ImmersiveFlow overrides
    let thisImmersiveFlowPaddingBottom = 0;
    if (modalFooter && modalHeader && prevCtaControl) {
      thisImmersiveFlowPaddingBottom = rem(210); // 130 (header height) + 80 (footer height)
    } else if (modalFooter && !modalHeader && prevCtaControl) {
      thisImmersiveFlowPaddingBottom = rem(120); // 80 (footer height) + 40 (header bottom padding)
    } else if (modalFooter && modalHeader) {
      thisImmersiveFlowPaddingBottom = rem(170);
    } else if (modalFooter && !modalHeader) {
      thisImmersiveFlowPaddingBottom = rem(80);
    } else if (!modalFooter && modalHeader) {
      thisImmersiveFlowPaddingBottom = rem(90); // 50 (header height) + 40 (additional controls height)
    }

    return {
      display: 'flex',
      height: 'auto',
      paddingBottom: thisImmersiveFlowPaddingBottom,
      paddingTop: 0,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'auto',
    };
  })()};
    `;
  }}
`;

// ============
// Modal Header
// ============

// Persistent Dismiss Button on Modal and ImmersiveFlow variants
export const DismissStyled = styled(Dismiss)`
  ${({ theme, variant }) => {
    const mediumUp = theme.mediumUp();
    return css`
      position: ${variant === 'immersiveFlow' ? 'fixed' : 'absolute'};
      top: ${mediumUp ? rem(34) : rem(4)};
      right: ${mediumUp ? rem(16) : rem(6)};
      z-index: 1;
    `;
  }}
`;

export const RowStyledHeaderControls = styled(Row)`
  ${({ variant }) => css`
    position: ${variant === 'immersiveFlow' ? 'relative' : 'absolute'};
    top: 0;
    width: 100%;
  `}
`;

export const ColStyledHeaderControls = styled(Col)`
  ${({ prevCtaControl, theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      height: ${mediumUp ? rem(100) : rem(50)};
      padding: 0;
      ${prevCtaControl && { marginBottom: rem(40) }}
    `;
  }}
`;

export const ButtonStyledHeaderPrevCta = styled(Button)`
  position: relative;
  top: 0;
  z-index: 1;
  .nmx-icon {
    rotate: 180deg;
  }
`;

export const DivStyledLogoContainer = styled.div`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      display: flex;
      position: relative;
      top: 0;
      width: 100%;
      height: ${mediumUp ? rem(100) : rem(50)};
      justify-content: center;
      background-color: white;
      z-index: 1;
    `;
  }}
`;

export const SpanStyledHeaderNmLogo = styled.span` // convert to a? move into modalHeader?
  ${({ basePath, nmxTemplateVersion, theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      display: block;
      width: ${mediumUp ? rem(250) : rem(180)};
      max-width: ${rem(250)};
      height: ${mediumUp ? rem(30) : rem(20)};
      align-self: center;
      background-image: url('${basePath}/template/assets/${nmxTemplateVersion}/images/logos/logo-horizontal-navy.svg');
      background-position-x: 0;
      background-repeat: no-repeat;
      background-size: cover;
    `;
  }}
`;

// ============
// Modal Footer
// ============

export const RowStyledFooterControls = styled(Row)`
  ${({ modalFooter, theme, variant }) => {
    return css`
      position: sticky;
      bottom: 0; /** sticky modal footer always set to bottom */
      width: 100%;
      height: ${rem(80)};
      text-align: right;
      background-color: ${modalFooter.footerBackgroundColor ? modalFooter.footerBackgroundColor : theme.colors.neutral.white};
    `;
  }}
`;

export const ColStyledFooterControls = styled(Col)`
  display: inline-flex;
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};
  text-align: right;
  justify-content: flex-end;
  align-items: center;
  .nmx-button{
    &:last-child {
      margin-left: ${rem(20)};
    }
  }
`;
